import { useEffect, useRef, useState } from 'react';
import { call, iptv, type SportEvent } from './api';

/** Sportcentrum: live uitslagen, clubs, standen en wedstrijden (ESPN via de tumedia-server). */

export interface ScLeague { id: string; path: string; name: string; short: string; sport: string; country: string | null; default: boolean }
export interface SSide { id: string; name: string; short: string; abbr: string; logo: string | null; color: string | null; score: string | null; winner: boolean; form: string | null; red: number }
export interface SIncident { side: 'home' | 'away'; minute: string; player: string; kind: 'goal' | 'pen' | 'own' | 'red' }
export interface SMatch {
  id: string; eventId: string; league: string; leagueName: string; path: string; sport: string;
  start: number; state: 'pre' | 'in' | 'post'; detail: string; minute: string | null; cancelled: boolean;
  home: SSide; away: SSide; incidents: SIncident[]; venue: string | null; fav?: boolean;
}
export interface FavTeam { id: string; path: string; name: string; short: string; abbr: string; logo: string | null; color: string | null }
export interface TeamInfo extends FavTeam { league: string; leagueName: string }
export interface SRow { rank: number; team: { id: string; name: string; short: string; abbr: string; logo: string | null }; played: number; w: number; d: number; l: number; gf: number; ga: number; gd: string; points: number; pct: string | null; gb: string | null; note: { color: string; text: string } | null }
export interface SGroup { name: string; rows: SRow[] }
export interface FormItem { id: string; path: string; eventId: string; r: 'W' | 'D' | 'L' | null; home: boolean; opp: string; oppLogo: string | null; score: string; league: string; start: number }
export interface TeamSummary {
  team: TeamInfo;
  live: SMatch | null; next: SMatch | null; recent: SMatch[]; upcoming: SMatch[];
  form: FormItem[]; standing: (SRow & { of: number; group: string }) | null; goals: { for: number; against: number };
}
export interface SEventRow { minute: string; side: 'home' | 'away' | null; kind: 'goal' | 'pen' | 'own' | 'yellow' | 'red' | 'sub' | 'period' | 'other'; text: string; player: string | null; extra: string | null }
export interface SStat { label: string; home: string; away: string; homeN: number; awayN: number }
export interface SPlayer { n: string; name: string; pos: string; subIn: boolean; subOut: boolean }
export interface SLineup { side: 'home' | 'away'; formation: string | null; starters: SPlayer[]; bench: SPlayer[] }
export interface MatchDetail { match: SMatch; events: SEventRow[]; stats: SStat[]; lineups: SLineup[]; form: { home: string; away: string }; venue: string | null; attendance: number | null }
export interface ScSettings { teams: FavTeam[]; leagues: string[]; custom: boolean }

export const scApi = {
  leagues: () => call<{ leagues: ScLeague[] }>('/api/sportcenter/leagues'),
  settings: () => call<ScSettings>('/api/sportcenter/settings'),
  save: (b: { teams?: FavTeam[]; leagues?: string[] | null }) => call<ScSettings>('/api/sportcenter/settings', { method: 'PUT', body: JSON.stringify(b) }),
  teams: (league: string) => call<{ teams: TeamInfo[] }>(`/api/sportcenter/teams?league=${encodeURIComponent(league)}`),
  search: (q: string) => call<{ teams: TeamInfo[] }>(`/api/sportcenter/teams?q=${encodeURIComponent(q)}`),
  scores: (from: number, to: number, all = false) => call<{ matches: SMatch[]; poll: number }>(`/api/sportcenter/scores?from=${from}&to=${to}${all ? '&all=1' : ''}`),
  team: (path: string, id: string) => call<TeamSummary>(`/api/sportcenter/team?path=${encodeURIComponent(path)}&id=${id}`),
  myTeams: () => call<{ teams: TeamSummary[]; poll: number }>('/api/sportcenter/my-teams'),
  standings: (q: { league?: string; path?: string }) => call<{ league: { id: string; name: string; path: string }; groups: SGroup[] }>(`/api/sportcenter/standings?${q.league ? `league=${encodeURIComponent(q.league)}` : `path=${encodeURIComponent(q.path ?? '')}`}`),
  match: (path: string, event: string) => call<MatchDetail>(`/api/sportcenter/match?path=${encodeURIComponent(path)}&event=${event}`),
};

// ---------- Gedeelde gegevens ----------

let settingsP: Promise<ScSettings> | null = null;
const settingsListeners = new Set<() => void>();
export function loadScSettings(force = false): Promise<ScSettings> {
  if (force || !settingsP) settingsP = scApi.settings().catch((e: unknown) => {
    settingsP = null;
    throw e;
  });
  return settingsP;
}
export function setScSettings(s: ScSettings) {
  settingsP = Promise.resolve(s);
  settingsListeners.forEach((l) => l());
}
export function useScSettings(): ScSettings | null {
  const [s, setS] = useState<ScSettings | null>(null);
  useEffect(() => {
    const load = () => void loadScSettings().then(setS).catch(() => setS({ teams: [], leagues: [], custom: false }));
    load();
    settingsListeners.add(load);
    return () => {
      settingsListeners.delete(load);
    };
  }, []);
  return s;
}

let leaguesP: Promise<ScLeague[]> | null = null;
export function loadScLeagues(): Promise<ScLeague[]> {
  leaguesP ??= scApi.leagues().then((r) => r.leagues).catch((e: unknown) => {
    leaguesP = null;
    throw e;
  });
  return leaguesP;
}

/** Laadt en ververst zolang de pagina zichtbaar is; de server zegt hoe vaak (sneller als er iets live is). */
export function usePolling<T extends { poll?: number }>(key: string, load: () => Promise<T>, fallbackSec = 120): { data: T | null; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    let off = false;
    let timer: number | undefined;
    const run = async () => {
      try {
        const d = await loadRef.current();
        if (off) return;
        setData(d);
        setError(null);
        timer = window.setTimeout(run, Math.max(15, d.poll ?? fallbackSec) * 1000);
      } catch (e) {
        if (off) return;
        setError(e instanceof Error ? e.message : 'Laden mislukt');
        timer = window.setTimeout(run, 60_000);
      }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        window.clearTimeout(timer);
        void run();
      }
    };
    void run();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      off = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [key, tick, fallbackSec]);
  return { data, error, reload: () => setTick((t) => t + 1) };
}

/** Begin en eind van een dag in de eigen tijdzone (0 = vandaag). */
export function dayRange(offset = 0): [number, number] {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  const from = d.getTime();
  d.setDate(d.getDate() + 1);
  return [from, d.getTime()];
}

// ---------- Tekst ----------

export const hhmm = (ms: number) => new Date(ms).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

export function dayText(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const diff = Math.round((new Date(d.toDateString()).getTime() - new Date(now.toDateString()).getTime()) / 86_400_000);
  if (diff === 0) return 'Vandaag';
  if (diff === 1) return 'Morgen';
  if (diff === -1) return 'Gisteren';
  const s = d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "Begint over 2 u, 14 m" */
export function countdown(ms: number, now = Date.now()): string {
  const m = Math.max(0, Math.round((ms - now) / 60_000));
  if (m < 60) return `${m} m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} u, ${m % 60} m`;
  return `${Math.floor(h / 24)} dagen`;
}

/** Status in één woord/regel: minuut, "Rust", "Afgelopen" of de aftraptijd. */
export function statusLabel(m: SMatch): string {
  if (m.cancelled) return m.detail || 'Afgelast';
  if (m.state === 'in') return m.minute ?? m.detail ?? 'Live';
  if (m.state === 'post') return m.detail && m.detail !== 'Afgelopen' ? m.detail : 'Afgelopen';
  return hhmm(m.start);
}

export const resultClass = (r: string | null) => (r === 'W' ? 'sc-w' : r === 'L' ? 'sc-l' : r === 'D' ? 'sc-d' : '');
export const resultNl = (r: string | null) => (r === 'W' ? 'W' : r === 'L' ? 'V' : r === 'D' ? 'G' : '–');

export const matchHref = (m: { path: string; eventId: string }) => `/sportcenter/match?path=${encodeURIComponent(m.path)}&event=${m.eventId}`;
export const clubHref = (t: { path: string; id: string }) => `/sportcenter/club?path=${encodeURIComponent(t.path)}&id=${t.id}`;
export const tableHref = (league: string) => `/sportcenter/standen?league=${encodeURIComponent(league)}`;

// ---------- Kijk live ----------

let sportP: { at: number; p: Promise<SportEvent[]> } | null = null;
/** Uitzendingen uit je IPTV-gids die bij deze wedstrijd horen (zelfde koppeling als in Live TV). */
export async function broadcastsFor(matchId: string): Promise<SportEvent['channels']> {
  if (!sportP || Date.now() - sportP.at > 120_000) {
    const p = iptv.accounts()
      .then((a) => (a.accounts.some((x) => x.enabledForProfile) ? iptv.sport(24).then((r) => r.events) : []))
      .catch(() => [] as SportEvent[]);
    sportP = { at: Date.now(), p };
  }
  const events = await sportP.p;
  const out: SportEvent['channels'] = [];
  for (const e of events) {
    if (e.rerun || e.match?.id !== matchId) continue;
    for (const c of e.channels) if (!out.some((x) => x.accountId === c.accountId && x.streamId === c.streamId)) out.push(c);
  }
  return out.sort((a, b) => Number(b.nl) - Number(a.nl));
}
